import {
  all, takeLatest, takeEvery, put, call, take, fork, select, race,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  doc,
  collection,
  query,
  where,
  onSnapshot,
  getDoc,
  getDocs,
  getCountFromServer,
  documentId,
  orderBy,
  limit,
  startAfter,
  Timestamp,
} from 'firebase/firestore';
import axios from 'axios';
import { message } from 'antd';
import moment from 'moment-timezone';
import actions from './actions';
import authActions from '../auth/actions';
import notification from '../../components/notification';

const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;

// Serializa Timestamps do Firestore — o redux guarda número, não objeto.
function serializeProfile(id, data) {
  const out = { id };
  Object.entries(data || {}).forEach(([key, value]) => {
    out[key] = value && typeof value.toMillis === 'function' ? value.toMillis() : value;
  });
  return out;
}

const getMainUserFromStore = (state) => state.Auth.mainUser;
const getUserFromStore = (state) => state.Auth.user;
const getAdminPayer = (state) => state.MeagendaBilling.adminPayer;

function getIdToken() {
  const auth = getAuth();
  const { currentUser } = auth;
  return currentUser.getIdToken();
}

function resolveMasterUid(user, mainUser) {
  if (mainUser) return mainUser;
  return user?.uid || null;
}

// Single channel that watches: pointer → profile → invoices.
// When the pointer's profileId changes, the channel transparently
// recreates the inner profile+invoices listeners.
function createBillingChannel(masterUid, coveredUid) {
  const db = getFirestore();
  return eventChannel((emit) => {
    let currentProfileId = null;
    let pointerResolved = false;
    let unsubProfile = null;
    let unsubInvoices = null;

    const stopInner = () => {
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }
      if (unsubInvoices) {
        unsubInvoices();
        unsubInvoices = null;
      }
    };

    const startInner = (profileId) => {
      const profileRef = doc(db, 'billingProfiles', profileId);
      unsubProfile = onSnapshot(
        profileRef,
        (snap) => {
          if (snap.exists()) {
            emit({ type: 'PROFILE', profileId, profile: snap.data() });
          } else {
            emit({ type: 'NOT_FOUND' });
          }
        },
        (error) => emit({ type: 'ERROR', error, source: `profile:${profileId}` }),
      );
      const invQuery = query(
        collection(db, 'billingProfiles', profileId, 'invoices'),
        orderBy('dueDate', 'desc'),
        limit(50),
      );
      unsubInvoices = onSnapshot(
        invQuery,
        (snap) => {
          const invoices = [];
          snap.forEach((s) => invoices.push({ id: s.id, ...s.data() }));
          emit({ type: 'INVOICES', invoices });
        },
        (error) => emit({ type: 'ERROR', error, source: `invoices:${profileId}` }),
      );
    };

    // Sub-contas que não estão em coveredUids (ex: secretária) não têm pointer
    // próprio. Quando isso acontece, fazemos fallback para o pointer do master
    // — a sub-conta herda o status (TRIAL/ACTIVE/SUSPENDED) da conta principal.
    let unsubPointer = null;
    let watchingMasterFallback = false;

    const watchPointer = (pointerUid) => {
      const pointerRef = doc(db, 'professionals', masterUid, 'meagendaBilling', pointerUid);
      return onSnapshot(
        pointerRef,
        (snap) => {
          const data = snap.exists() ? snap.data() : null;
          const profileId = data?.billingProfileId || null;
          const isArchived = !!data?.archived;
          const effectiveProfileId = isArchived ? null : profileId;

          if (!effectiveProfileId && !watchingMasterFallback && pointerUid !== masterUid) {
            watchingMasterFallback = true;
            if (unsubPointer) unsubPointer();
            unsubPointer = watchPointer(masterUid);
            return;
          }

          // O 1º snapshot sempre emite: sem pointer, effectiveProfileId nasce
          // null igual a currentProfileId e o NOT_FOUND nunca sairia.
          if (pointerResolved && effectiveProfileId === currentProfileId) return;
          pointerResolved = true;
          stopInner();
          currentProfileId = effectiveProfileId;
          if (!effectiveProfileId) {
            emit({ type: 'NOT_FOUND' });
          } else {
            startInner(effectiveProfileId);
          }
        },
        (error) => emit({ type: 'ERROR', error, source: 'pointer' }),
      );
    };

    unsubPointer = watchPointer(coveredUid);

    return () => {
      if (unsubPointer) unsubPointer();
      stopInner();
    };
  });
}

// Processa os eventos do canal (pointer→profile→invoices) até ser cancelado.
function* watchBillingChannel(channel) {
  while (true) {
    const event = yield take(channel);
    if (event.type === 'PROFILE') {
      yield put({
        type: actions.BILLING_PROFILE_UPDATED,
        payload: { profileId: event.profileId, profile: event.profile },
      });
    } else if (event.type === 'INVOICES') {
      yield put({ type: actions.BILLING_INVOICES_UPDATED, payload: event.invoices });
    } else if (event.type === 'NOT_FOUND') {
      yield put({ type: actions.BILLING_PROFILE_NOT_FOUND });
    } else if (event.type === 'ERROR') {
      console.warn('[meagendaBilling] erro do listener:', event.source, event.error);
      yield put({
        type: actions.BILLING_PROFILE_ERROR,
        payload: event.error?.message || 'Erro ao carregar billing.',
      });
    }
  }
}

export function* loadBillingProfileSaga() {
  yield takeLatest([
    authActions.LOGIN_SUCCESS,
    actions.LOAD_BILLING_PROFILE_REQUEST,
  ], function* () {
    const user = yield select(getUserFromStore);
    const mainUser = yield select(getMainUserFromStore);
    const masterUid = resolveMasterUid(user, mainUser);
    const coveredUid = user?.uid;
    if (!masterUid || !coveredUid) {
      yield put({
        type: actions.BILLING_PROFILE_ERROR,
        payload: 'Sessão sem usuário para carregar a assinatura.',
      });
      return;
    }

    let channel = null;
    try {
      channel = yield call(createBillingChannel, masterUid, coveredUid);
      // Encerra o canal no logout/clear — o takeLatest só cancela essa task ao
      // chegar outro LOGIN_SUCCESS/LOAD; sem o race os onSnapshot vazariam entre
      // sessões (erros de permissão + dados de billing da conta anterior).
      yield race({
        watch: call(watchBillingChannel, channel),
        loggedOut: take([authActions.LOGOUT, authActions.CLEAR_STATES]),
      });
    } catch (error) {
      console.warn('[meagendaBilling] loadBillingProfileSaga falhou:', error);
      yield put({
        type: actions.BILLING_PROFILE_ERROR,
        payload: error?.message || 'Erro ao carregar billing.',
      });
    } finally {
      if (channel) channel.close();
    }
  });
}

// ── User actions ────────────────────────────────────────────────────

function callActivateTrial(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaActivateTrial`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* activateTrialSaga() {
  yield takeLatest(actions.ACTIVATE_TRIAL_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callActivateTrial, action.payload, idToken);
      const data = response.data || {};
      yield put({ type: actions.ACTIVATE_TRIAL_SUCCESS, payload: data });
      const url = data.currentInvoiceUrl;
      if (data.reactivateByPayment) {
        notification('info', 'Pague a fatura em aberto para reativar o acesso.');
      } else if (data.alreadyActivated) {
        notification('info', 'Sua assinatura já está ativa.');
      } else if (url) {
        notification('success', 'Assinatura ativada! Abrindo fatura para pagamento.');
      } else {
        notification('success', 'Assinatura ativada. A fatura aparecerá em Minha Assinatura.');
      }
      if (url && typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener');
      }
    } catch (error) {
      console.warn('[meagendaBilling] activateTrial falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao ativar assinatura.';
      notification('error', msg);
      yield put({ type: actions.ACTIVATE_TRIAL_ERROR, payload: msg });
    }
  });
}

function callCreateProfile(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaCreateProfile`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* createProfileSaga() {
  yield takeLatest(actions.CREATE_PROFILE_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callCreateProfile, action.payload, idToken);
      yield put({ type: actions.CREATE_PROFILE_SUCCESS, payload: response.data });
      notification('success', 'Profile de cobrança criado.');
    } catch (error) {
      console.warn('[meagendaBilling] createProfile falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao criar profile.';
      notification('error', msg);
      yield put({ type: actions.CREATE_PROFILE_ERROR, payload: msg });
    }
  });
}

// ── Admin: search professionals (mesmo padrão de keywords usado em ModuleManager) ────────

function searchProfessionalsByName(term) {
  const db = getFirestore();
  const normalized = (term || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const colRef = collection(db, 'professionals');
  const q = query(
    colRef,
    where('keywords', 'array-contains', normalized),
    limit(20),
  );
  return getDocs(q);
}

export function* adminSearchProfilesSaga() {
  yield takeLatest(actions.ADMIN_SEARCH_PROFILES_REQUEST, function* (action) {
    try {
      const term = (action.payload?.term || '').trim();
      if (term.length < 2) {
        yield put({ type: actions.ADMIN_SEARCH_PROFILES_SUCCESS, payload: [] });
        return;
      }
      const snap = yield call(searchProfessionalsByName, term);
      const results = [];
      snap.forEach((s) => {
        const data = s.data();
        results.push({
          id: s.id,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
        });
      });
      yield put({ type: actions.ADMIN_SEARCH_PROFILES_SUCCESS, payload: results });
    } catch (error) {
      console.warn('[meagendaBilling] adminSearchProfiles falhou:', error);
      yield put({
        type: actions.ADMIN_SEARCH_PROFILES_ERROR,
        payload: error?.message || 'Erro ao buscar profissionais.',
      });
    }
  });
}

// ── Admin: load profiles by payerUid ────────────────────────────────

function fetchProfilesByPayer(payerUid) {
  const db = getFirestore();
  const colRef = collection(db, 'billingProfiles');
  const q = query(colRef, where('payerUid', '==', payerUid));
  return getDocs(q);
}

export function* adminLoadProfilesByPayerSaga() {
  yield takeLatest(actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST, function* (action) {
    try {
      const { payerUid } = action.payload;
      const [snap, payerSnap] = yield all([
        call(fetchProfilesByPayer, payerUid),
        call(getDoc, doc(getFirestore(), 'professionals', payerUid)),
      ]);
      const profiles = [];
      snap.forEach((s) => {
        profiles.push(serializeProfile(s.id, s.data()));
      });
      const payerData = payerSnap.exists() ? payerSnap.data() : {};
      yield put({
        type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_SUCCESS,
        payload: {
          profiles,
          payerInfo: {
            uid: payerUid,
            name: `${payerData.firstName || ''} ${payerData.lastName || ''}`.trim() || payerUid,
            email: payerData.email || '',
            mobile: payerData.mobile || '',
            cpfCnpj: (payerData.cpfCnpj || payerData.cpf || '').replace(/\D+/g, '') || '',
            mainUser: payerData.mainUser || null,
            meagendaAsaasCustomerId: payerData.meagendaAsaasCustomerId || null,
          },
        },
      });
    } catch (error) {
      console.warn('[meagendaBilling] adminLoadProfilesByPayer falhou:', error);
      yield put({
        type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_ERROR,
        payload: error?.message || 'Erro ao carregar profiles.',
      });
    }
  });
}

// ── Admin: link / cancel / move ─────────────────────────────────────

function callAdminLinkProfile(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaLinkProfile`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminLinkProfileSaga() {
  yield takeLatest(actions.ADMIN_LINK_PROFILE_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callAdminLinkProfile, action.payload, idToken);
      // Mesmo slot da migração: para o assistente, vincular e criar terminam
      // na mesma tela de sucesso.
      yield put({
        type: actions.ADMIN_LINK_PROFILE_SUCCESS,
        payload: { ...(response.data || {}), payerUid: action.payload?.payerUid || null },
      });
      notification('success', 'Assinatura vinculada com sucesso.');
      if (action.payload?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: action.payload.payerUid },
        });
      }
      yield put({ type: actions.ADMIN_LOAD_QUEUE_COUNTS_REQUEST });
    } catch (error) {
      console.warn('[meagendaBilling] adminLinkProfile falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao vincular assinatura.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_LINK_PROFILE_ERROR, payload: msg });
    }
  });
}

function callAdminCancelProfile(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaCancelProfile`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminCancelProfileSaga() {
  yield takeLatest(actions.ADMIN_CANCEL_PROFILE_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      yield call(callAdminCancelProfile, action.payload, idToken);
      yield put({ type: actions.ADMIN_CANCEL_PROFILE_SUCCESS });
      notification('success', 'Profile cancelada.');
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      console.warn('[meagendaBilling] adminCancelProfile falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao cancelar profile.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_CANCEL_PROFILE_ERROR, payload: msg });
    }
  });
}

function callAdminSuspendProfile(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaSuspendProfile`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminSuspendProfileSaga() {
  yield takeLatest(actions.ADMIN_SUSPEND_PROFILE_REQUEST, function* (action) {
    const hide = message.loading('Suspendendo assinatura...', 0);
    try {
      const idToken = yield call(getIdToken);
      const resp = yield call(callAdminSuspendProfile, action.payload, idToken);
      yield put({ type: actions.ADMIN_SUSPEND_PROFILE_SUCCESS });
      notification('success', resp?.data?.suspended === 'now'
        ? 'Assinatura suspensa (bloqueada agora).'
        : 'Suspensão agendada para o fim do período pago.');
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      console.warn('[meagendaBilling] adminSuspendProfile falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao suspender profile.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_SUSPEND_PROFILE_ERROR, payload: msg });
    } finally {
      // roda também no cancelamento do takeLatest — spinner não fica preso
      hide();
    }
  });
}

function callAdminReactivateProfile(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaReactivateProfile`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminReactivateProfileSaga() {
  yield takeLatest(actions.ADMIN_REACTIVATE_PROFILE_REQUEST, function* (action) {
    const hide = message.loading('Reativando assinatura...', 0);
    try {
      const idToken = yield call(getIdToken);
      yield call(callAdminReactivateProfile, action.payload, idToken);
      yield put({ type: actions.ADMIN_REACTIVATE_PROFILE_SUCCESS });
      notification('success', 'Assinatura reativada.');
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      console.warn('[meagendaBilling] adminReactivateProfile falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao reativar profile.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_REACTIVATE_PROFILE_ERROR, payload: msg });
    } finally {
      // roda também no cancelamento do takeLatest — spinner não fica preso
      hide();
    }
  });
}

function callAdminMoveUid(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaMoveUid`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminMoveUidSaga() {
  yield takeLatest(actions.ADMIN_MOVE_UID_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      yield call(callAdminMoveUid, action.payload, idToken);
      yield put({ type: actions.ADMIN_MOVE_UID_SUCCESS });
      notification('success', 'Operação concluída.');
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      console.warn('[meagendaBilling] adminMoveUid falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao mover uid.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_MOVE_UID_ERROR, payload: msg });
    }
  });
}

// ── Admin: list Asaas subscriptions (com fallback p/ customer do payer) ────

function fetchProfessionalCustomerId(payerUid) {
  const db = getFirestore();
  const ref = doc(db, 'professionals', payerUid);
  return getDoc(ref);
}

function callListAsaasSubscriptions(body, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaListSubscriptions`,
    body,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminListAsaasSubscriptionsSaga() {
  yield takeLatest(actions.ADMIN_LIST_ASAAS_SUBS_REQUEST, function* (action) {
    try {
      const {
        payerUid, offset, limit: pageLimit, status, ignorePayerCustomer,
        explicitCustomerId,
      } = action.payload || {};

      let resolvedCustomerId = null;
      let hadCustomerFallback = false;
      // Prioridade: customerId explícito (admin escolheu via typeahead) >
      // resolução pelo payer > fallback pra "todas".
      if (explicitCustomerId) {
        resolvedCustomerId = explicitCustomerId;
      } else if (payerUid && !ignorePayerCustomer) {
        const profSnap = yield call(fetchProfessionalCustomerId, payerUid);
        if (profSnap.exists()) {
          resolvedCustomerId = profSnap.data()?.meagendaAsaasCustomerId || null;
        }
        // Fallback retroativo: se o doc do payer ainda não tem o campo (caso
        // de profiles vinculados antes da gente persistir o customerId no
        // professional), tenta pegar de algum profile existente.
        if (!resolvedCustomerId) {
          const adminPayer = yield select(getAdminPayer);
          if (adminPayer?.payerUid === payerUid) {
            const fromProfile = (adminPayer.profiles || [])
              .map((p) => p.asaasCustomerId)
              .find(Boolean);
            if (fromProfile) resolvedCustomerId = fromProfile;
          }
        }
        if (!resolvedCustomerId) hadCustomerFallback = true;
      }

      // Sem cliente resolvido, a chamada iria sem filtro e devolveria TODAS as
      // assinaturas da conta de billing — a tela mostraria as mesmas para
      // qualquer pagador. Só quem pede explicitamente (ignorePayerCustomer) vê
      // a lista completa.
      if (hadCustomerFallback && !ignorePayerCustomer) {
        yield put({
          type: actions.ADMIN_LIST_ASAAS_SUBS_SUCCESS,
          payload: {
            results: [],
            totalCount: 0,
            hasMore: false,
            offset: 0,
            limit: pageLimit || 20,
            filters: { status: status || null, customer: null },
            hadCustomerFallback: true,
          },
        });
        return;
      }

      const body = {
        offset: offset || 0,
        limit: pageLimit || 20,
      };
      if (resolvedCustomerId) body.customer = resolvedCustomerId;
      if (status) body.status = status;

      const idToken = yield call(getIdToken);
      const response = yield call(callListAsaasSubscriptions, body, idToken);
      const data = response.data || {};
      yield put({
        type: actions.ADMIN_LIST_ASAAS_SUBS_SUCCESS,
        payload: {
          results: data.data || [],
          totalCount: data.totalCount || 0,
          hasMore: !!data.hasMore,
          offset: data.offset || 0,
          limit: data.limit || (pageLimit || 20),
          filters: { status: status || null, customer: resolvedCustomerId },
          hadCustomerFallback,
        },
      });
    } catch (error) {
      console.warn('[meagendaBilling] adminListAsaasSubscriptions falhou:', error);
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao listar assinaturas Asaas.';
      yield put({ type: actions.ADMIN_LIST_ASAAS_SUBS_ERROR, payload: msg });
    }
  });
}

// ── Admin: list HPs cobertos por um payer (master + customUsers HP) ──

function fetchPayerCustomUsers(payerUid) {
  const db = getFirestore();
  const colRef = collection(db, 'professionals', payerUid, 'customUsers');
  return getDocs(colRef);
}

function fetchProfessionalDoc(uid) {
  const db = getFirestore();
  return getDoc(doc(db, 'professionals', uid));
}

function pickCpf(data) {
  return (data?.cpfCnpj || data?.cpf || '').replace(/\D+/g, '') || null;
}

export function* adminLoadPayerHpsSaga() {
  yield takeLatest(actions.ADMIN_LOAD_PAYER_HPS_REQUEST, function* (action) {
    try {
      const { payerUid } = action.payload || {};
      if (!payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PAYER_HPS_SUCCESS,
          payload: { options: [] },
        });
        return;
      }
      // Os profissionais da conta ficam sob o MASTER. Quando o pagador é um
      // profissional adicional (paga em separado), ler os customUsers dele
      // devolveria lista vazia — resolvemos o master antes.
      const payerSnap = yield call(fetchProfessionalDoc, payerUid);
      const payerData = payerSnap.exists() ? payerSnap.data() : {};
      const masterUid = payerData.mainUser || payerUid;
      const [masterSnap, customUsersSnap] = yield all([
        masterUid === payerUid
          ? call(() => Promise.resolve(payerSnap))
          : call(fetchProfessionalDoc, masterUid),
        call(fetchPayerCustomUsers, masterUid),
      ]);
      const masterData = masterSnap.exists() ? masterSnap.data() : {};
      const masterName = `${masterData.firstName || ''} ${masterData.lastName || ''}`.trim();
      const members = [{
        uid: masterUid,
        label: masterName ? `${masterName} (master)` : 'Conta principal (master)',
        value: masterUid,
        email: masterData.email || '',
        kind: 'master',
        billable: true,
        cpfCnpj: pickCpf(masterData),
      }];
      customUsersSnap.forEach((s) => {
        const data = s.data() || {};
        const isHp = !!(data.healthProfessional || data.customHealthProfessional);
        members.push({
          uid: s.id,
          label: `${data.firstName || ''} ${data.lastName || ''}`.trim() || s.id,
          value: s.id,
          email: data.email || '',
          kind: isHp ? 'hp' : 'secretary',
          billable: isHp,
          cpfCnpj: pickCpf(data),
        });
      });
      yield put({
        type: actions.ADMIN_LOAD_PAYER_HPS_SUCCESS,
        payload: {
          masterUid,
          members,
          // options mantém o contrato antigo (só cobráveis) pro CoverageDrawer
          // e o MoveUidModal, que já consomem essa forma.
          options: members.filter((m) => m.billable).map(({ label, value, cpfCnpj }) => ({
            label, value, cpfCnpj,
          })),
        },
      });
    } catch (error) {
      console.warn('[meagendaBilling] adminLoadPayerHps falhou:', error);
      yield put({
        type: actions.ADMIN_LOAD_PAYER_HPS_ERROR,
        payload: error?.message || 'Erro ao carregar profissionais do payer.',
      });
    }
  });
}

// ── Admin: setCoveredUids (reconciliação em lote) ─────────────────

function callSetCoveredUids(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaSetCoveredUids`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminSetCoveredUidsSaga() {
  yield takeLatest(actions.ADMIN_SET_COVERED_UIDS_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callSetCoveredUids, action.payload, idToken);
      yield put({
        type: actions.ADMIN_SET_COVERED_UIDS_SUCCESS,
        payload: response.data,
      });
      const data = response.data || {};
      if (data.noop) {
        notification('info', 'Cobertura já estava nesse estado — nada a aplicar.');
      } else {
        const parts = [];
        if (data.added?.length) parts.push(`+${data.added.length} adicionado(s)`);
        if (data.removed?.length) parts.push(`-${data.removed.length} removido(s)`);
        notification('success', `Cobertura atualizada: ${parts.join(', ')}.`);
      }
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      console.warn('[meagendaBilling] adminSetCoveredUids falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao atualizar cobertura.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_SET_COVERED_UIDS_ERROR, payload: msg });
    }
  });
}

// ── Admin: recomputeProfile (sincronizar valor manualmente) ────────

function callRecomputeProfile(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaRecomputeProfile`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminRecomputeProfileSaga() {
  yield takeLatest(actions.ADMIN_RECOMPUTE_PROFILE_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callRecomputeProfile, action.payload, idToken);
      yield put({
        type: actions.ADMIN_RECOMPUTE_PROFILE_SUCCESS,
        payload: response.data,
      });
      const data = response.data || {};
      const snap = data.snapshot;
      if (data.mode === 'manual-legacy') {
        notification('info', 'Modo manual: valor espelhado do Asaas (sem alteração automática).');
      } else if (!snap) {
        notification('info', 'Recálculo pulado (profile cancelado).');
      } else {
        notification('success', `Valor sincronizado: R$ ${snap.totalValue?.toFixed?.(2) || snap.totalValue}.`);
      }
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      console.warn('[meagendaBilling] adminRecomputeProfile falhou:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || 'Erro ao sincronizar valor.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_RECOMPUTE_PROFILE_ERROR, payload: msg });
    }
  });
}

// ── Admin: setProfileMode (trocar mode entre 'api' e 'manual-legacy') ──

function callSetProfileMode(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaSetProfileMode`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminSetProfileModeSaga() {
  yield takeLatest(actions.ADMIN_SET_PROFILE_MODE_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callSetProfileMode, action.payload, idToken);
      yield put({
        type: actions.ADMIN_SET_PROFILE_MODE_SUCCESS,
        payload: response.data,
      });
      const data = response.data || {};
      if (data.noop) {
        notification('info', 'Profile já estava nesse modo — nada a aplicar.');
      } else if (data.mode === 'api' && data.snapshot) {
        notification(
          'success',
          `Modo trocado pra API. Valor sincronizado: R$ ${data.snapshot.totalValue?.toFixed?.(2) || data.snapshot.totalValue}.`,
        );
      } else {
        notification('success', `Modo trocado pra ${data.mode}.`);
      }
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      console.warn('[meagendaBilling] adminSetProfileMode falhou:', error);
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao trocar modo do profile.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_SET_PROFILE_MODE_ERROR, payload: msg });
    }
  });
}

// ── Admin: setTrial (editar período de trial da conta) ─────────────

function callSetTrial(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaSetTrial`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminSetTrialSaga() {
  yield takeLatest(actions.ADMIN_SET_TRIAL_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callSetTrial, action.payload, idToken);
      yield put({
        type: actions.ADMIN_SET_TRIAL_SUCCESS,
        payload: response.data,
      });
      const data = response.data || {};
      notification(
        'success',
        data.revived
          ? 'Trial reativado — conta voltou pra TRIAL.'
          : 'Período de trial atualizado.',
      );
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      console.warn('[meagendaBilling] adminSetTrial falhou:', error);
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao atualizar o período de trial.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_SET_TRIAL_ERROR, payload: msg });
    }
  });
}

// ── Admin: force pro-rata merge (admin força mesmo com risco) ──────

function callForceProrataMerge(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaForceProrataMerge`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminForceProrataMergeSaga() {
  yield takeLatest(actions.ADMIN_FORCE_PRORATA_MERGE_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callForceProrataMerge, action.payload, idToken);
      yield put({
        type: actions.ADMIN_FORCE_PRORATA_MERGE_SUCCESS,
        payload: response.data,
      });
      const data = response.data || {};
      notification(
        'success',
        `Merge aplicado: R$ ${data.addedProRata?.toFixed?.(2) || data.addedProRata} adicionado à fatura ${data.paymentId}.`,
      );
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao forçar merge da pro-rata.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_FORCE_PRORATA_MERGE_ERROR, payload: msg });
    }
  });
}

// ── Admin: forgive pro-rata (zera acumulador) ──────────────────────

function callForgiveProrata(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaForgiveProrata`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminForgiveProrataSaga() {
  yield takeLatest(actions.ADMIN_FORGIVE_PRORATA_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callForgiveProrata, action.payload, idToken);
      yield put({
        type: actions.ADMIN_FORGIVE_PRORATA_SUCCESS,
        payload: response.data,
      });
      const data = response.data || {};
      notification(
        'success',
        `Pro-rata perdoada: R$ ${data.forgivenValue?.toFixed?.(2) || data.forgivenValue} zerados.`,
      );
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
    } catch (error) {
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao perdoar pro-rata.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_FORGIVE_PRORATA_ERROR, payload: msg });
    }
  });
}

// ── Admin: fila de trabalho (contagens + listas por situação) ──────

function countProfiles(constraints) {
  const db = getFirestore();
  return getCountFromServer(query(collection(db, 'billingProfiles'), ...constraints));
}

export function* adminLoadQueueCountsSaga() {
  yield takeLatest(actions.ADMIN_LOAD_QUEUE_COUNTS_REQUEST, function* () {
    try {
      const db = getFirestore();
      const in7d = Timestamp.fromDate(moment().add(7, 'days').endOf('day').toDate());
      const [trial, pending, overdue, value, active, reconciliation, stats] = yield all([
        call(countProfiles, [where('status', '==', 'TRIAL'), where('trialUntil', '<=', in7d)]),
        call(countProfiles, [where('status', '==', 'PENDING')]),
        call(countProfiles, [where('status', '==', 'OVERDUE')]),
        call(countProfiles, [where('valueOutOfSync', '==', true)]),
        call(countProfiles, [where('status', '==', 'ACTIVE')]),
        call(countProfiles, [where('subscriptionNeedsReconciliation', '==', true)]),
        call(getDoc, doc(db, 'config', 'billingQueueStats')),
      ]);
      const statsData = stats.exists() ? stats.data() : null;
      yield put({
        type: actions.ADMIN_LOAD_QUEUE_COUNTS_SUCCESS,
        payload: {
          counts: {
            trial: trial.data().count,
            pending: pending.data().count,
            overdue: overdue.data().count,
            value: value.data().count,
            active: active.data().count,
          },
          exceptions: { reconciliation: reconciliation.data().count },
          unlinked: {
            count: statsData ? statsData.unlinkedMasters : null,
            computedAt: statsData && statsData.computedAt?.toMillis
              ? statsData.computedAt.toMillis()
              : null,
          },
        },
      });
    } catch (error) {
      console.warn('[meagendaBilling] contagens da fila falharam:', error);
      yield put({
        type: actions.ADMIN_LOAD_QUEUE_COUNTS_ERROR,
        payload: error?.message || 'Erro ao contar as filas.',
      });
    }
  });
}

// Constraints por fila. Só usamos orderBy onde já existe índice composto
// (status+trialUntil e status+gracePeriodEndsAt); o resto ordena no cliente.
function queueConstraints(queue, status) {
  const in7d = Timestamp.fromDate(moment().add(7, 'days').endOf('day').toDate());
  switch (queue) {
    case 'trial':
      return [where('status', '==', 'TRIAL'), where('trialUntil', '<=', in7d), orderBy('trialUntil')];
    case 'pending':
      return [where('status', '==', 'PENDING')];
    case 'overdue':
      return [where('status', '==', 'OVERDUE'), orderBy('gracePeriodEndsAt')];
    case 'value':
      return [where('valueOutOfSync', '==', true)];
    case 'active':
      return [where('status', '==', 'ACTIVE')];
    default:
      return [where('status', '==', status || 'ACTIVE')];
  }
}

// Nome e e-mail não vivem no perfil de cobrança — hidrata em lotes de 30
// (limite do operador `in`).
function* hydratePayers(rows) {
  const db = getFirestore();
  const uids = [...new Set(rows.map((r) => r.payerUid).filter(Boolean))];
  const payerMap = {};
  for (let i = 0; i < uids.length; i += 30) {
    const chunk = uids.slice(i, i + 30);
    const snap = yield call(
      getDocs,
      query(collection(db, 'professionals'), where(documentId(), 'in', chunk)),
    );
    snap.forEach((d) => {
      const v = d.data() || {};
      payerMap[d.id] = {
        name: `${v.firstName || ''} ${v.lastName || ''}`.trim() || d.id,
        email: v.email || '',
        mainUser: v.mainUser || null,
      };
    });
  }
  return rows.map((row) => ({ ...row, payer: payerMap[row.payerUid] || null }));
}

export function* adminLoadQueueSaga() {
  yield takeLatest(actions.ADMIN_LOAD_QUEUE_REQUEST, function* (action) {
    const { queue, status, cursor } = action.payload || {};
    try {
      const db = getFirestore();
      const constraints = queueConstraints(queue, status);
      if (cursor) {
        const cursorSnap = yield call(getDoc, doc(db, 'billingProfiles', cursor));
        if (cursorSnap.exists()) constraints.push(startAfter(cursorSnap));
      }
      const snap = yield call(
        getDocs,
        query(collection(db, 'billingProfiles'), ...constraints, limit(100)),
      );
      // migrationInProgress = perfil temporário de uma migração em andamento;
      // some sozinho em segundos e não deve aparecer na fila.
      const rows = snap.docs
        .map((d) => serializeProfile(d.id, d.data()))
        .filter((p) => !p.migrationInProgress);
      const hydrated = yield call(hydratePayers, rows);
      yield put({
        type: actions.ADMIN_LOAD_QUEUE_SUCCESS,
        payload: {
          queue,
          status,
          rows: hydrated,
          hasMore: snap.size === 100,
          cursor: snap.size ? snap.docs[snap.docs.length - 1].id : null,
          append: !!cursor,
        },
      });
    } catch (error) {
      console.warn('[meagendaBilling] lista da fila falhou:', error);
      yield put({
        type: actions.ADMIN_LOAD_QUEUE_ERROR,
        payload: error?.message || 'Erro ao carregar a lista.',
      });
    }
  });
}

// ── Admin: assinatura antiga em lote (coluna da fila de migração) ───

function callLegacyMatch(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaLegacyMatch`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminLegacyMatchSaga() {
  yield takeEvery(actions.ADMIN_LEGACY_MATCH_REQUEST, function* (action) {
    const { payerUids } = action.payload;
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callLegacyMatch, { payerUids }, idToken);
      yield put({
        type: actions.ADMIN_LEGACY_MATCH_SUCCESS,
        payload: {
          payerUids,
          configured: response.data?.configured !== false,
          results: response.data?.results || {},
          errors: response.data?.errors || {},
        },
      });
    } catch (error) {
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao consultar a conta Asaas antiga.';
      yield put({ type: actions.ADMIN_LEGACY_MATCH_ERROR, payload: { payerUids, error: msg } });
    }
  });
}

// ── Admin: assinatura na conta Asaas antiga ────────────────────────

function callLegacyLookup(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaLegacyLookup`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminLegacyLookupSaga() {
  yield takeLatest(actions.ADMIN_LEGACY_LOOKUP_REQUEST, function* (action) {
    const { payerUid, query: searchQuery } = action.payload;
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(
        callLegacyLookup,
        { payerUid, query: searchQuery || undefined },
        idToken,
      );
      yield put({
        type: actions.ADMIN_LEGACY_LOOKUP_SUCCESS,
        payload: { payerUid, data: response.data },
      });
    } catch (error) {
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao consultar a conta Asaas antiga.';
      yield put({
        type: actions.ADMIN_LEGACY_LOOKUP_ERROR,
        payload: { payerUid, error: msg },
      });
    }
  });
}

// ── Admin: prévia da migração ──────────────────────────────────────

function callPreviewMigration(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaPreviewMigration`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminPreviewMigrationSaga() {
  yield takeLatest(actions.ADMIN_PREVIEW_MIGRATION_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callPreviewMigration, action.payload, idToken);
      yield put({
        type: actions.ADMIN_PREVIEW_MIGRATION_SUCCESS,
        payload: response.data,
      });
    } catch (error) {
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao simular a migração.';
      yield put({ type: actions.ADMIN_PREVIEW_MIGRATION_ERROR, payload: msg });
    }
  });
}

// ── Admin: migrar cobrança para a conta Asaas nova ─────────────────

function callMigrateProfile(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaMigrateProfile`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminMigrateProfileSaga() {
  yield takeLatest(actions.ADMIN_MIGRATE_PROFILE_REQUEST, function* (action) {
    const { payerUid } = action.payload;
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callMigrateProfile, action.payload, idToken);
      const data = response.data || {};
      yield put({
        type: actions.ADMIN_MIGRATE_PROFILE_SUCCESS,
        payload: { ...data, payerUid },
      });
      notification('success', 'Cobrança criada na conta nova do Asaas.');
      yield put({
        type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
        payload: { payerUid },
      });
      yield put({ type: actions.ADMIN_LOAD_QUEUE_COUNTS_REQUEST });
    } catch (error) {
      const data = error?.response?.data || {};
      const msg = data.error || data.message || 'Erro ao migrar a cobrança.';
      notification('error', msg);
      yield put({
        type: actions.ADMIN_MIGRATE_PROFILE_ERROR,
        payload: { error: msg, code: data.code || null, details: data },
      });
    }
  });
}

// ── Admin: histórico da conta (faturas + ajustes + cortesias) ──────

export function* adminLoadProfileHistorySaga() {
  yield takeLatest(actions.ADMIN_LOAD_PROFILE_HISTORY_REQUEST, function* (action) {
    const { profileId } = action.payload;
    try {
      const db = getFirestore();
      const base = collection(db, 'billingProfiles', profileId, 'invoices');
      const [invoicesSnap, eventsSnap, courtesiesSnap, mergesSnap] = yield all([
        call(getDocs, query(base, orderBy('dueDate', 'desc'), limit(50))),
        call(getDocs, query(
          collection(db, 'billingProfiles', profileId, 'changeEvents'),
          orderBy('createdAt', 'desc'),
          limit(50),
        )),
        call(getDocs, query(
          collection(db, 'billingProfiles', profileId, 'courtesies'),
          orderBy('createdAt', 'desc'),
          limit(50),
        )),
        // Um doc por fatura (id = paymentId): a pró-rata que já entrou em cada
        // uma. Sem orderBy — doc só de cortesia não tem lastMergedAt.
        call(getDocs, query(
          collection(db, 'billingProfiles', profileId, 'proRataMerges'),
          limit(60),
        )),
      ]);
      yield put({
        type: actions.ADMIN_LOAD_PROFILE_HISTORY_SUCCESS,
        payload: {
          profileId,
          invoices: invoicesSnap.docs.map((d) => serializeProfile(d.id, d.data())),
          changeEvents: eventsSnap.docs.map((d) => serializeProfile(d.id, d.data())),
          courtesies: courtesiesSnap.docs.map((d) => serializeProfile(d.id, d.data())),
          proRataMerges: mergesSnap.docs.map((d) => serializeProfile(d.id, d.data())),
        },
      });
    } catch (error) {
      // changeEvents/courtesies exigem as rules novas; sem elas a tela mostra
      // só as faturas em vez de quebrar.
      console.warn('[meagendaBilling] histórico do profile falhou:', error);
      yield put({
        type: actions.ADMIN_LOAD_PROFILE_HISTORY_ERROR,
        payload: { profileId, error: error?.message || 'Erro ao carregar o histórico.' },
      });
    }
  });
}

// ── Admin: cortesia (mês grátis / desconto numa mensalidade / permanente) ──

function callApplyCourtesy(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaApplyCourtesy`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

function buildPermanentDiscountMessage(data) {
  const total = `R$ ${Number(data.totalValue || 0).toFixed(2)}`;
  const base = data.removed
    ? `Desconto permanente removido — a mensalidade voltou a ${total}.`
    : `Desconto permanente aplicado — a mensalidade ficou em ${total}.`;
  const locked = (data.lockedInvoices || []).length;
  if (!locked) return base;
  return `${base} ${locked} fatura(s) já aberta(s) pelo cliente ficaram no valor anterior.`;
}

function buildCourtesyMessage(data) {
  if (data.kind === 'PERMANENT_DISCOUNT') return buildPermanentDiscountMessage(data);
  if (data.kind === 'FREE_MONTH') {
    const count = (data.deletedPaymentIds || []).length;
    const nextDue = data.nextDueDateAfter
      ? ` Próxima cobrança em ${moment(data.nextDueDateAfter, 'YYYY-MM-DD').format('DD/MM/YYYY')}.`
      : '';
    const base = count === 0
      ? `Cortesia aplicada: cobrança adiada.${nextDue}`
      : `Cortesia aplicada: ${count} fatura(s) dispensada(s).${nextDue}`;
    if (data.partial) {
      return `${base} Parte dos ciclos não pôde ser dispensada — confira a assinatura.`;
    }
    return base;
  }
  const discount = Number(data.discountValue || 0).toFixed(2);
  const value = Number(data.newValue || 0).toFixed(2);
  return `Desconto de R$ ${discount} aplicado — a fatura ficou em R$ ${value}.`;
}

export function* adminApplyCourtesySaga() {
  yield takeLatest(actions.ADMIN_APPLY_COURTESY_REQUEST, function* (action) {
    const { profileId } = action.payload;
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callApplyCourtesy, action.payload, idToken);
      const data = response.data || {};
      yield put({ type: actions.ADMIN_APPLY_COURTESY_SUCCESS, payload: data });
      notification('success', buildCourtesyMessage(data));
      const adminPayer = yield select(getAdminPayer);
      if (adminPayer?.payerUid) {
        yield put({
          type: actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST,
          payload: { payerUid: adminPayer.payerUid },
        });
      }
      // A fatura viva mudou de valor (ou sumiu) — recarrega o cartão do profile.
      yield put({
        type: actions.ADMIN_LOAD_CURRENT_INVOICE_REQUEST,
        payload: { profileId },
      });
    } catch (error) {
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao aplicar a cortesia.';
      notification('error', msg);
      yield put({ type: actions.ADMIN_APPLY_COURTESY_ERROR, payload: msg });
      // A recusa costuma ser porque a fatura mudou no Asaas (foi vista, paga ou
      // cancelada). Recarrega pra próxima abertura do modal já refletir isso —
      // sem isso o admin repetiria o envio contra os mesmos dados velhos.
      yield put({
        type: actions.ADMIN_LOAD_CURRENT_INVOICE_REQUEST,
        payload: { profileId },
      });
    }
  });
}

// ── Admin: load current invoice (live do Asaas, sem cache do Firestore) ──

function callGetCurrentInvoice(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaGetCurrentInvoice`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminLoadCurrentInvoiceSaga() {
  // takeEvery (não takeLatest) — múltiplos profiles podem carregar em
  // paralelo, cada um por profileId distinto.
  yield takeEvery(actions.ADMIN_LOAD_CURRENT_INVOICE_REQUEST, function* (action) {
    const { profileId } = action.payload;
    try {
      const idToken = yield call(getIdToken);
      const response = yield call(callGetCurrentInvoice, { profileId }, idToken);
      yield put({
        type: actions.ADMIN_LOAD_CURRENT_INVOICE_SUCCESS,
        payload: {
          profileId,
          payment: response.data?.payment || null,
          pendingPayments: response.data?.pendingPayments || [],
        },
      });
    } catch (error) {
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao buscar fatura atual.';
      yield put({
        type: actions.ADMIN_LOAD_CURRENT_INVOICE_ERROR,
        payload: { profileId, error: msg },
      });
    }
  });
}

// ── Admin: search Asaas customers (typeahead pra busca server-side) ──

function callSearchAsaasCustomers(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaSearchCustomers`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminSearchAsaasCustomersSaga() {
  yield takeLatest(actions.ADMIN_SEARCH_ASAAS_CUSTOMERS_REQUEST, function* (action) {
    try {
      const searchQuery = action.payload?.query;
      if (!searchQuery || String(searchQuery).trim().length < 2) {
        yield put({
          type: actions.ADMIN_SEARCH_ASAAS_CUSTOMERS_SUCCESS,
          payload: { results: [] },
        });
        return;
      }
      const idToken = yield call(getIdToken);
      const response = yield call(callSearchAsaasCustomers, { query: searchQuery }, idToken);
      yield put({
        type: actions.ADMIN_SEARCH_ASAAS_CUSTOMERS_SUCCESS,
        payload: { results: response.data?.data || [] },
      });
    } catch (error) {
      console.warn('[meagendaBilling] adminSearchAsaasCustomers falhou:', error);
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao buscar clientes Asaas.';
      yield put({ type: actions.ADMIN_SEARCH_ASAAS_CUSTOMERS_ERROR, payload: msg });
    }
  });
}

// ── Admin: list unlinked professionals (sem billingProfile) ────────

function callListUnlinked(payload, idToken) {
  return axios.post(
    `${ROOT_URL}/asaasMeagendaListUnlinked`,
    payload,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* adminListUnlinkedSaga() {
  yield takeLatest(actions.ADMIN_LIST_UNLINKED_REQUEST, function* (action) {
    try {
      const {
        pageSize, cursor, term, reset, onlyMasters, withAccountSummary,
      } = action.payload || {};
      const idToken = yield call(getIdToken);
      const response = yield call(
        callListUnlinked,
        {
          pageSize, cursor, term, onlyMasters, withAccountSummary,
        },
        idToken,
      );
      const data = response.data || {};
      yield put({
        type: actions.ADMIN_LIST_UNLINKED_SUCCESS,
        payload: {
          results: data.data || [],
          hasMore: !!data.hasMore,
          nextCursor: data.nextCursor || null,
          append: !reset && !!cursor,
        },
      });
    } catch (error) {
      console.warn('[meagendaBilling] adminListUnlinked falhou:', error);
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || 'Erro ao listar profissionais não vinculados.';
      yield put({ type: actions.ADMIN_LIST_UNLINKED_ERROR, payload: msg });
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(loadBillingProfileSaga),
    fork(activateTrialSaga),
    fork(createProfileSaga),
    fork(adminSearchProfilesSaga),
    fork(adminLoadProfilesByPayerSaga),
    fork(adminLinkProfileSaga),
    fork(adminCancelProfileSaga),
    fork(adminSuspendProfileSaga),
    fork(adminReactivateProfileSaga),
    fork(adminMoveUidSaga),
    fork(adminListAsaasSubscriptionsSaga),
    fork(adminLoadPayerHpsSaga),
    fork(adminSetCoveredUidsSaga),
    fork(adminRecomputeProfileSaga),
    fork(adminSetProfileModeSaga),
    fork(adminSetTrialSaga),
    fork(adminForceProrataMergeSaga),
    fork(adminForgiveProrataSaga),
    fork(adminApplyCourtesySaga),
    fork(adminLoadQueueCountsSaga),
    fork(adminLoadQueueSaga),
    fork(adminLegacyMatchSaga),
    fork(adminLegacyLookupSaga),
    fork(adminPreviewMigrationSaga),
    fork(adminMigrateProfileSaga),
    fork(adminLoadProfileHistorySaga),
    fork(adminLoadCurrentInvoiceSaga),
    fork(adminSearchAsaasCustomersSaga),
    fork(adminListUnlinkedSaga),
  ]);
}
